import * as hierarchyActions from "contexts/hierarchy/interface";
import { HierarchyContextProps } from "contexts/hierarchy/interface";
import { eHierarchyLevel } from "functions/api/enums";
import { getDomainReplacementsSaveBlockers } from "./getDomainReplacementsSaveBlockers";
import { getSinglesSaveBlockers } from "./getSinglesSaveBlockers";
import { ISaveBlocker, ISaveBlockers } from "./interface";
import { isValidReportTypeSelection } from "./isValidReportTypeSelection";
import { shouldRebuildDerivedSaveBlockers } from "./shouldRebuildDerivedSaveBlockers";

/**
 * Derives the app-level save blockers after a hierarchy action. Blockers live in app settings
 * rather than the hierarchy slice, so they are composed here from the reduced hierarchy.
 *
 * @param saveBlockers - Current application save blockers.
 * @param hierarchyState - The hierarchy slice after the action was applied.
 * @param action - The hierarchy action that was applied.
 * @param allowedReportTypes - Centrally loaded report types from app settings.
 * @returns The updated save blockers.
 */
export const reduceHierarchySaveBlockers = (
  saveBlockers: ISaveBlockers,
  hierarchyState: HierarchyContextProps,
  action: hierarchyActions.IHierarchyAction,
  allowedReportTypes: string[],
): ISaveBlockers => {
  const shouldRefreshReplacementSaveBlockers = shouldRebuildDerivedSaveBlockers(action);
  const shouldRefreshReportTypeSaveBlocker = new Set([
    hierarchyActions.CLEAR_HIERARCHY,
    hierarchyActions.LOAD_HIERARCHY,
    hierarchyActions.SAVE_HIERARCHY_COMPLETED,
    hierarchyActions.UPDATE_HIERARCHY,
  ]).has(action.operation);

  const updatedBlockers = [
    hierarchyActions.CLEAR_HIERARCHY,
    hierarchyActions.LOAD_HIERARCHY,
  ].includes(action.operation)
    ? new Set<ISaveBlocker>()
    : !shouldRefreshReplacementSaveBlockers && !shouldRefreshReportTypeSaveBlocker
      ? saveBlockers
      : action.operation === hierarchyActions.SAVE_HIERARCHY_COMPLETED
        ? new Set<ISaveBlocker>()
        : new Set(
            Array.from(saveBlockers).filter(
              (blocker) =>
                (!shouldRefreshReplacementSaveBlockers ||
                  (blocker.type !== "singles" && blocker.type !== "domainReplacements")) &&
                (!shouldRefreshReportTypeSaveBlocker || blocker.type !== "reportType") &&
                (!("arsSaveBlockers" in action) || blocker.type !== "arsObjects"),
            ),
          );

  if (shouldRefreshReplacementSaveBlockers) {
    getSinglesSaveBlockers(hierarchyState.hierarchy).forEach((blocker) =>
      updatedBlockers.add(blocker),
    );
    getDomainReplacementsSaveBlockers(hierarchyState.hierarchy).forEach((blocker) =>
      updatedBlockers.add(blocker),
    );
  }

  if (
    shouldRefreshReportTypeSaveBlocker &&
    hierarchyState.hierarchy?.level === eHierarchyLevel.Report &&
    allowedReportTypes.length > 0 &&
    !isValidReportTypeSelection(hierarchyState.hierarchy.reportType, allowedReportTypes)
  ) {
    updatedBlockers.add({
      id: "reportType",
      type: "reportType",
      warningText: "Before saving the report, specify a valid report type.",
    });
  }

  if ("arsSaveBlockers" in action && action.arsSaveBlockers) {
    action.arsSaveBlockers.forEach((blocker) => updatedBlockers.add(blocker));
  }

  return updatedBlockers;
};
